import { AlertCircle } from "lucide-react";
import type { ProjectDocument } from "@gb/contracts";
import { organiseGapWords, picturesNotReadWords } from "./brain-item-row";

/**
 * What of a file nothing has read, and what no step has organised, on its own
 * page (2026-09-26, tracker RET-12; a normal user sees it, the default the
 * owner can change).
 *
 * Until then a person could not tell, on any screen of their own, that part
 * of a file was never read: the step records said it, and only the backoffice
 * and the debug view show those. An answer then quietly lacked what the
 * unread part holds: a drawing's layout, a site photo, a scanned page.
 *
 * - **Not read in full**, once every try of a step that failed has run out
 *   (RET-01). The sentence says which part.
 * - **Pictures not read**, with the count the full read gave.
 * - **Not organised yet**, with each step that has not run.
 *
 * Not while the file is being read: the read about to finish decides all
 * three again. Nothing at all for a file read and organised in full, which is
 * most files.
 */
export function DocumentReadState({ document }: { document: ProjectDocument }) {
  const reading = document.processingStatus === "queued" || document.processingStatus === "running";
  if (reading) return null;
  const lines = [
    document.readFailure ? `Not read in full. ${document.readFailure} Every try to read it again failed.` : null,
    document.picturesNotRead !== null && document.picturesNotRead > 0
      ? `${picturesNotReadWords(document.picturesNotRead)}. What ${document.picturesNotRead === 1 ? "it shows" : "they show"} is not in answers.`
      : null,
    document.organiseGaps.length > 0 ? `${organiseGapWords(document.organiseGaps)}.` : null,
  ].filter((line): line is string => line !== null);
  if (lines.length === 0) return null;

  return (
    <ul
      aria-label="What is not read or organised"
      className="mt-3 flex flex-col gap-1 rounded-card-lg border-[1.5px] border-border bg-surface px-4 py-2.5"
    >
      {lines.map((line) => (
        <li key={line} className="flex items-start gap-2 text-[0.84rem] leading-[1.5] text-ink-slate">
          <AlertCircle size={14} aria-hidden className="mt-[3px] shrink-0 text-warn" />
          {line}
        </li>
      ))}
    </ul>
  );
}
